import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import { useSelector } from "react-redux";
import { getApiBaseUrl } from "../../../config/api";
import {
    TimetableConflictsPanel,
    TimetableSubstitutionsPanel,
    RoomBookingsPanel,
} from "./TimetableAdvancedPanels";
import {
    WEEK_DAYS,
    FREE_SUBJECT,
    cellKey,
    buildCellDraftsFromRows,
    displayLessonCell,
    formatConflictSummary,
} from "./timetableUtils";

function KpiMini({ title, value, icon: Icon, subtitle }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    {title}
                </Typography>
                <Icon sx={{ color: "primary.main", fontSize: 22 }} />
            </Stack>
            <Typography variant="h6" fontWeight={800} color="primary.main">
                {value}
            </Typography>
            {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            ) : null}
        </Paper>
    );
}

const ClassTimetablePage = () => {
    const { currentUser } = useSelector((state) => state.user);
    const schoolId = currentUser?._id;

    const now = new Date();
    const baseYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    const academicYearOptions = useMemo(
        () => [`${baseYear - 1}/${baseYear}`, `${baseYear}/${baseYear + 1}`, `${baseYear + 1}/${baseYear + 2}`],
        [baseYear]
    );

    const [educationLevel, setEducationLevel] = useState(currentUser?.schoolType === "Primary" ? "Primary" : "Secondary");
    const [academicYear, setAcademicYear] = useState(academicYearOptions[1] || `${baseYear}/${baseYear + 1}`);
    const [classId, setClassId] = useState("");
    const [calendarData, setCalendarData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [feedback, setFeedback] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [cellEdits, setCellEdits] = useState({});
    const [teachersList, setTeachersList] = useState([]);
    const [activeTab, setActiveTab] = useState("timetable");
    const [conflictDialog, setConflictDialog] = useState(null);

    const load = useCallback(
        async (nextClassId = classId, nextEducationLevel = educationLevel, nextAcademicYear = academicYear) => {
            if (!schoolId) return;
            setLoading(true);
            setError("");
            setFeedback("");
            try {
                const params = new URLSearchParams({
                    educationLevel: nextEducationLevel,
                    academicYear: nextAcademicYear,
                });
                if (nextClassId) params.set("classId", nextClassId);
                const { data } = await axios.get(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}?${params.toString()}`);
                setCalendarData(data);
                const resolved = data?.filters?.classId || "";
                if (resolved !== classId) setClassId(resolved);
                setEditMode(false);
                setCellEdits({});
            } catch (err) {
                setError(err.response?.data?.message || "Could not load timetables.");
                setCalendarData(null);
            } finally {
                setLoading(false);
            }
        },
        [schoolId, classId, educationLevel, academicYear]
    );

    useEffect(() => {
        if (!schoolId) return;
        setClassId("");
        load("", educationLevel, academicYear);
    }, [schoolId, educationLevel, academicYear, load]);

    useEffect(() => {
        if (!schoolId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Teachers/${schoolId}`);
                if (!cancelled && Array.isArray(data)) setTeachersList(data);
            } catch {
                if (!cancelled) setTeachersList([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [schoolId]);

    const selectedClass = calendarData?.selectedClass;
    const timetable = calendarData?.timetable;
    const classOptions = Array.isArray(calendarData?.classOptions) ? calendarData.classOptions : [];

    const subjectNames = useMemo(() => {
        const subs = selectedClass?.subjects;
        if (!Array.isArray(subs)) return [];
        return subs.map((s) => s.subName).filter(Boolean);
    }, [selectedClass]);

    const teacherOptions = useMemo(() => {
        const fromCalendar = Array.isArray(calendarData?.teacherOptions) ? calendarData.teacherOptions : [];
        if (fromCalendar.length) return fromCalendar;
        return teachersList;
    }, [calendarData, teachersList]);

    const subjectTeacherMap = useMemo(() => {
        const map = {};
        for (const subject of selectedClass?.subjects || []) {
            map[subject.subName] = {
                teacherId: subject.teacherId || "",
                teacherName: subject.teacherName || "",
                subjectId: subject.id || subject._id || "",
            };
        }
        return map;
    }, [selectedClass]);

    const facilityOptions = Array.isArray(calendarData?.facilityOptions) ? calendarData.facilityOptions : [];
    const timetableConflicts = calendarData?.timetableConflicts;
    const classConflictKeys = useMemo(
        () => calendarData?.timetableMeta?.classConflictKeys || [],
        [calendarData?.timetableMeta?.classConflictKeys],
    );
    const classConflictSet = useMemo(() => new Set(classConflictKeys), [classConflictKeys]);
    const timetableSubstitutions = Array.isArray(calendarData?.timetableSubstitutions) ? calendarData.timetableSubstitutions : [];
    const roomBookings = Array.isArray(calendarData?.roomBookings) ? calendarData.roomBookings : [];
    const periodOptions = useMemo(() => {
        if (!timetable?.rows) return [];
        return timetable.rows
            .filter((row) => row.type !== "break")
            .map((row) => ({ key: row.key, label: row.label, startTime: row.startTime, endTime: row.endTime }));
    }, [timetable]);

    const startEditing = () => {
        if (!timetable?.rows) return;
        setCellEdits(buildCellDraftsFromRows(timetable.rows));
        setEditMode(true);
        setError("");
    };

    const cancelEditing = () => {
        setEditMode(false);
        setCellEdits({});
    };

    const saveTimetable = async (ignoreConflicts = false) => {
        if (!schoolId || !classId || !selectedClass) return;
        setSaving(true);
        setError("");
        try {
            await axios.post(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}/timetable`, {
                schoolId,
                academicYear,
                educationLevel,
                classId,
                className: selectedClass.sclassName || "",
                cellOverrides: cellEdits,
                id: timetable?.timetableId || undefined,
                ignoreConflicts,
            });
            setFeedback("Timetable saved.");
            setEditMode(false);
            setCellEdits({});
            setConflictDialog(null);
            await load(classId, educationLevel, academicYear);
        } catch (err) {
            if (err.response?.status === 409 && err.response?.data?.conflicts) {
                setConflictDialog(err.response.data.conflicts);
                setError(err.response.data.message || "Conflicts detected.");
            } else {
                setError(err.response?.data?.message || "Could not save timetable.");
            }
        } finally {
            setSaving(false);
        }
    };

    const resetToAuto = async () => {
        if (!timetable?.timetableId) {
            setFeedback("Nothing to reset — this timetable is already using the auto layout.");
            return;
        }
        if (!window.confirm("Remove all custom edits for this class and year? The grid will go back to the auto-generated layout.")) return;
        try {
            await axios.delete(`${getApiBaseUrl()}/SchoolCalendar/item/${timetable.timetableId}`);
            setFeedback("Custom timetable cleared; showing auto layout again.");
            setEditMode(false);
            setCellEdits({});
            await load(classId, educationLevel, academicYear);
        } catch (err) {
            setError(err.response?.data?.message || "Could not reset timetable.");
        }
    };

    const displayCell = (row, day, baseCell) => {
        if (editMode) {
            const key = cellKey(row.key, day);
            const draft = cellEdits[key];
            if (draft) {
                const subj = String(draft.subjectName || "").trim();
                const teach = String(draft.teacherName || "").trim();
                return {
                    ...baseCell,
                    subjectName: subj || timetable?.fillerLabel || "Support",
                    teacherName: teach,
                    teacherId: draft.teacherId || baseCell.teacherId,
                    facilityId: draft.facilityId || baseCell.facilityId,
                    room: draft.roomName || baseCell.room,
                    roomName: draft.roomName || baseCell.roomName || baseCell.room,
                    filler: !subj,
                    userEdited: true,
                };
            }
        }
        return displayLessonCell(row, day, baseCell, cellEdits);
    };

    const updateCellDraft = (key, patch) => {
        setCellEdits((prev) => {
            const cur = prev[key] || {
                subjectName: "",
                subjectId: "",
                teacherName: "",
                teacherId: "",
                facilityId: "",
                roomName: "",
            };
            return { ...prev, [key]: { ...cur, ...patch } };
        });
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 26%)`
                        : alpha(t.palette.common.black, 0.18),
                pb: 6,
            }}
        >
            <Container maxWidth={false} sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 2.5, md: 3 }, maxWidth: "100%", width: "100%" }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        mb: 3,
                        borderRadius: 3,
                        color: "common.white",
                        background: (t) =>
                            `linear-gradient(120deg, ${t.palette.secondary.dark} 0%, ${t.palette.secondary.main} 55%, ${alpha(
                                t.palette.primary.main,
                                0.88
                            )} 100%)`,
                        boxShadow: (t) => `0 12px 40px ${alpha(t.palette.secondary.main, 0.22)}`,
                    }}
                >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: alpha("#fff", 0.14),
                                }}
                            >
                                <ScheduleOutlinedIcon sx={{ fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                    Weekly schedule
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                    Class timetables
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 720, mt: 0.5 }}>
                                    Auto-generated from subject periods, then editable per slot with teacher, room, and conflict checks.
                                    Manage substitutions and room bookings from the tabs below.
                                </Typography>
                            </Box>
                        </Stack>
                        <Button
                            variant="contained"
                            onClick={() => load(classId, educationLevel, academicYear)}
                            sx={{
                                bgcolor: "common.white",
                                color: "secondary.main",
                                fontWeight: 700,
                                "&:hover": { bgcolor: alpha("#fff", 0.92) },
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Paper>

                {feedback ? (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFeedback("")}>
                        {feedback}
                    </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 180 }} disabled={editMode}>
                            <InputLabel>Education Level</InputLabel>
                            <Select label="Education Level" value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)}>
                                <MenuItem value="Primary">Primary</MenuItem>
                                <MenuItem value="Secondary">Secondary</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }} disabled={editMode}>
                            <InputLabel>Academic Year</InputLabel>
                            <Select label="Academic Year" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                                {academicYearOptions.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }} disabled={editMode}>
                            <InputLabel>Class</InputLabel>
                            <Select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}>
                                {classOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.sclassName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" onClick={() => load(classId, educationLevel, academicYear)} disabled={loading || editMode}>
                            Apply
                        </Button>
                    </Stack>
                </Paper>

                {error ? (
                    <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiMini
                            title="School day"
                            value={timetable ? `${timetable.startTime} – ${timetable.endTime}` : "—"}
                            icon={SchoolOutlinedIcon}
                            subtitle={educationLevel === "Primary" ? "30-minute periods" : "40-minute periods"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiMini
                            title="Class"
                            value={selectedClass?.sclassName || "Select a class"}
                            icon={AutoStoriesOutlinedIcon}
                            subtitle={selectedClass ? `${selectedClass.subjectCount} subjects` : "Use filters above"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiMini
                            title="Conflicts"
                            value={timetableConflicts?.total ?? "—"}
                            icon={WarningAmberOutlinedIcon}
                            subtitle={timetableConflicts?.total ? formatConflictSummary(timetableConflicts) : "School-wide check"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiMini
                            title="Rooms booked"
                            value={roomBookings.length || "0"}
                            icon={MeetingRoomOutlinedIcon}
                            subtitle={`${timetableSubstitutions.length} substitution(s)`}
                        />
                    </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                    <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto">
                        <Tab value="timetable" label="Timetable grid" />
                        <Tab value="conflicts" label={`Conflicts${timetableConflicts?.total ? ` (${timetableConflicts.total})` : ""}`} />
                        <Tab value="substitutions" label={`Substitutions (${timetableSubstitutions.length})`} />
                        <Tab value="rooms" label={`Room bookings (${roomBookings.length})`} />
                    </Tabs>
                </Paper>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    {activeTab === "timetable" ? (
                        <>
                    {!editMode ? (
                        <Button
                            variant="contained"
                            startIcon={<EditOutlinedIcon />}
                            onClick={startEditing}
                            disabled={!timetable?.rows || loading}
                        >
                            Edit timetable
                        </Button>
                    ) : (
                        <>
                            <Button variant="contained" color="success" startIcon={<SaveOutlinedIcon />} onClick={saveTimetable} disabled={saving}>
                                {saving ? "Saving…" : "Save changes"}
                            </Button>
                            <Button variant="outlined" onClick={cancelEditing} disabled={saving}>
                                Cancel
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<RestartAltOutlinedIcon />}
                        onClick={resetToAuto}
                        disabled={!timetable?.timetableId || editMode || loading}
                    >
                        Reset to auto layout
                    </Button>
                    {timetable?.timetableId ? (
                        <Chip size="small" label="Custom edits saved" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
                    ) : (
                        <Chip size="small" label="Auto-generated only" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                        </>
                    ) : null}
                </Stack>

                {activeTab === "conflicts" ? (
                    <TimetableConflictsPanel conflicts={timetableConflicts} classConflictKeys={classConflictKeys} />
                ) : null}

                {activeTab === "substitutions" ? (
                    <TimetableSubstitutionsPanel
                        schoolId={schoolId}
                        educationLevel={educationLevel}
                        academicYear={academicYear}
                        substitutions={timetableSubstitutions}
                        classOptions={classOptions}
                        teacherOptions={teacherOptions}
                        facilityOptions={facilityOptions}
                        periodOptions={periodOptions}
                        onRefresh={() => load(classId, educationLevel, academicYear)}
                    />
                ) : null}

                {activeTab === "rooms" ? (
                    <RoomBookingsPanel
                        schoolId={schoolId}
                        educationLevel={educationLevel}
                        academicYear={academicYear}
                        roomBookings={roomBookings}
                        classOptions={classOptions}
                        facilityOptions={facilityOptions}
                        periodOptions={periodOptions}
                        onRefresh={() => load(classId, educationLevel, academicYear)}
                    />
                ) : null}

                {activeTab === "timetable" ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 0,
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `2px solid ${alpha(t.palette.primary.main, 0.35)}`,
                        boxShadow: (t) => `0 16px 48px ${alpha(t.palette.primary.main, 0.12)}, 0 4px 16px ${alpha(t.palette.common.black, 0.06)}`,
                        bgcolor: "background.paper",
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 2.75 },
                            py: { xs: 2, sm: 2.25 },
                            background: (t) =>
                                `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(t.palette.secondary.main, 0.08)} 100%)`,
                            borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}`,
                        }}
                    >
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
                            <Stack direction="row" spacing={1.75} alignItems="flex-start">
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                                        color: "primary.main",
                                    }}
                                >
                                    <ScheduleOutlinedIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="overline" color="text.secondary" fontWeight={800} letterSpacing={1.1}>
                                        Mon – Fri
                                    </Typography>
                                    <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                        {editMode ? "Editing timetable" : "Timetable"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 900 }}>
                                        {editMode
                                            ? "Pick subject and teacher for each lesson slot. Free period uses your school’s filler label."
                                            : "Subject and teacher per slot."}
                                    </Typography>
                                </Box>
                            </Stack>
                            {selectedClass ? (
                                <Chip
                                    label={selectedClass.sclassName}
                                    color="primary"
                                    sx={{ fontWeight: 800, px: 0.5, fontSize: "0.95rem", alignSelf: { xs: "flex-start", sm: "center" } }}
                                />
                            ) : (
                                <Chip label="No class selected" variant="outlined" sx={{ alignSelf: { xs: "flex-start", sm: "center" } }} />
                            )}
                        </Stack>
                    </Box>
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: (t) => alpha(t.palette.grey[50], t.palette.mode === "light" ? 1 : 0.06) }}>
                        {loading && !calendarData ? (
                            <Skeleton variant="rounded" width="100%" height={420} sx={{ borderRadius: 2 }} />
                        ) : !timetable ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Choose <strong>Primary</strong> or <strong>Secondary</strong>, pick a <strong>class</strong>, then click{" "}
                                <strong>Apply</strong>.
                            </Alert>
                        ) : (
                            <TableContainer
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    overflowX: "auto",
                                    borderRadius: 2,
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                }}
                            >
                                <Table
                                    size={editMode ? "small" : "medium"}
                                    sx={{
                                        tableLayout: "fixed",
                                        width: "100%",
                                        minWidth: editMode ? 960 : 720,
                                        borderCollapse: "separate",
                                        borderSpacing: 0,
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    width: "12%",
                                                    minWidth: 100,
                                                    fontWeight: 800,
                                                    fontSize: "0.75rem",
                                                    letterSpacing: 0.8,
                                                    textTransform: "uppercase",
                                                    color: "text.secondary",
                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                                    borderRight: (t) => `1px solid ${t.palette.divider}`,
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 2,
                                                }}
                                            >
                                                Period / time
                                            </TableCell>
                                            {WEEK_DAYS.map((day) => (
                                                <TableCell
                                                    key={day}
                                                    align="center"
                                                    sx={{
                                                        width: `${88 / 5}%`,
                                                        fontWeight: 800,
                                                        fontSize: "0.85rem",
                                                        color: "primary.dark",
                                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                                        borderRight: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
                                                        "&:last-of-type": { borderRight: "none" },
                                                    }}
                                                >
                                                    {day}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {timetable.rows.map((row, rowIndex) => (
                                            <TableRow
                                                key={row.key}
                                                sx={{
                                                    bgcolor: rowIndex % 2 === 0 ? "action.hover" : "background.paper",
                                                    "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
                                                }}
                                            >
                                                <TableCell
                                                    sx={(theme) => ({
                                                        verticalAlign: "top",
                                                        borderRight: `1px solid ${theme.palette.divider}`,
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 1,
                                                        bgcolor:
                                                            row.type === "break"
                                                                ? alpha("#ff9800", 0.14)
                                                                : rowIndex % 2 === 0
                                                                  ? "action.hover"
                                                                  : "background.paper",
                                                    })}
                                                >
                                                    <Typography variant="subtitle2" fontWeight={800}>
                                                        {row.label}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                                        {row.startTime} – {row.endTime}
                                                    </Typography>
                                                </TableCell>
                                                {WEEK_DAYS.map((day) => {
                                                    const baseCell = row[day];
                                                    const isBreak = baseCell?.type === "break";
                                                    const cell = isBreak ? baseCell : displayCell(row, day, baseCell);
                                                    const hasSubject = Boolean(cell?.subjectName && !cell?.filler);
                                                    const hasTeacher = Boolean(cell?.teacherName);
                                                    const hasRoom = Boolean(cell?.room || cell?.roomName);
                                                    const key = cellKey(row.key, day);
                                                    const draft = cellEdits[key];
                                                    const hasConflict = classConflictSet.has(key);

                                                    return (
                                                        <TableCell
                                                            key={key}
                                                            align="center"
                                                            sx={{
                                                                verticalAlign: "top",
                                                                minHeight: editMode ? 160 : 88,
                                                                py: 1,
                                                                px: 0.75,
                                                                borderRight: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
                                                                "&:last-of-type": { borderRight: "none" },
                                                                outline: hasConflict ? (t) => `2px solid ${t.palette.error.main}` : "none",
                                                                outlineOffset: -2,
                                                                bgcolor: isBreak
                                                                    ? (t) => alpha(t.palette.warning.main, 0.1)
                                                                    : hasConflict
                                                                      ? (t) => alpha(t.palette.error.main, 0.08)
                                                                    : rowIndex % 2 === 0
                                                                      ? (t) => alpha(t.palette.grey[50], t.palette.mode === "light" ? 0.5 : 0.08)
                                                                      : "transparent",
                                                            }}
                                                        >
                                                            {isBreak ? (
                                                                <Chip label={baseCell.label} size="small" color="warning" sx={{ fontWeight: 700 }} />
                                                            ) : editMode ? (
                                                                <Stack spacing={1} sx={{ width: "100%" }}>
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel>Subject</InputLabel>
                                                                        <Select
                                                                            label="Subject"
                                                                            value={
                                                                                draft && String(draft.subjectName || "").trim() === ""
                                                                                    ? FREE_SUBJECT
                                                                                    : subjectNames.includes(draft?.subjectName)
                                                                                      ? draft.subjectName
                                                                                      : draft?.subjectName || FREE_SUBJECT
                                                                            }
                                                                            onChange={(e) => {
                                                                                const v = e.target.value;
                                                                                const subjectName = v === FREE_SUBJECT ? "" : v;
                                                                                const linked = subjectTeacherMap[subjectName];
                                                                                updateCellDraft(key, {
                                                                                    subjectName,
                                                                                    subjectId: linked?.subjectId || "",
                                                                                    teacherName: linked?.teacherName || draft?.teacherName || "",
                                                                                    teacherId: linked?.teacherId || draft?.teacherId || "",
                                                                                });
                                                                            }}
                                                                        >
                                                                            <MenuItem value={FREE_SUBJECT}>
                                                                                <em>Free / support ({timetable.fillerLabel || "support"})</em>
                                                                            </MenuItem>
                                                                            {draft?.subjectName &&
                                                                            String(draft.subjectName).trim() &&
                                                                            !subjectNames.includes(draft.subjectName) ? (
                                                                                <MenuItem value={draft.subjectName}>{draft.subjectName}</MenuItem>
                                                                            ) : null}
                                                                            {subjectNames.map((n) => (
                                                                                <MenuItem key={n} value={n}>
                                                                                    {n}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel>Teacher</InputLabel>
                                                                        <Select
                                                                            label="Teacher"
                                                                            value={draft?.teacherId || ""}
                                                                            onChange={(e) => {
                                                                                const teacher = teacherOptions.find(
                                                                                    (item) => (item.id || item._id) === e.target.value
                                                                                );
                                                                                updateCellDraft(key, {
                                                                                    teacherId: e.target.value,
                                                                                    teacherName: teacher?.name || "",
                                                                                });
                                                                            }}
                                                                            displayEmpty
                                                                        >
                                                                            <MenuItem value="">
                                                                                <em>—</em>
                                                                            </MenuItem>
                                                                            {teacherOptions.map((teacher) => (
                                                                                <MenuItem key={teacher.id || teacher._id} value={teacher.id || teacher._id}>
                                                                                    {teacher.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel>Room</InputLabel>
                                                                        <Select
                                                                            label="Room"
                                                                            value={draft?.facilityId || ""}
                                                                            onChange={(e) => {
                                                                                const facility = facilityOptions.find(
                                                                                    (item) => (item.id || item._id) === e.target.value
                                                                                );
                                                                                updateCellDraft(key, {
                                                                                    facilityId: e.target.value,
                                                                                    roomName: facility?.name || "",
                                                                                });
                                                                            }}
                                                                            displayEmpty
                                                                        >
                                                                            <MenuItem value="">
                                                                                <em>{selectedClass?.room || "Default class room"}</em>
                                                                            </MenuItem>
                                                                            {facilityOptions.map((item) => (
                                                                                <MenuItem key={item.id || item._id} value={item.id || item._id}>
                                                                                    {item.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                </Stack>
                                                            ) : (
                                                                <Stack spacing={0.5} alignItems="center" textAlign="center">
                                                                    {hasConflict ? (
                                                                        <Chip label="Conflict" size="small" color="error" sx={{ height: 20, fontSize: 10 }} />
                                                                    ) : null}
                                                                    {cell?.userEdited ? (
                                                                        <Chip label="Edited" size="small" color="info" sx={{ height: 20, fontSize: 10 }} />
                                                                    ) : null}
                                                                    <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.25 }}>
                                                                        {cell?.subjectName || "—"}
                                                                    </Typography>
                                                                    {hasTeacher ? (
                                                                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                                                                            {cell.teacherName}
                                                                        </Typography>
                                                                    ) : null}
                                                                    {hasRoom ? (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {cell.roomName || cell.room}
                                                                        </Typography>
                                                                    ) : null}
                                                                    {!hasSubject && !hasTeacher ? (
                                                                        <Typography variant="caption" color="text.disabled">
                                                                            Support slot
                                                                        </Typography>
                                                                    ) : null}
                                                                </Stack>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
                ) : null}

                <Dialog open={Boolean(conflictDialog)} onClose={() => setConflictDialog(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>Save despite conflicts?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {formatConflictSummary(conflictDialog || {})}. Saving now will store your edits even though clashes remain.
                        </Typography>
                        <TimetableConflictsPanel conflicts={conflictDialog} classConflictKeys={classConflictKeys} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConflictDialog(null)}>Review edits</Button>
                        <Button color="warning" variant="contained" onClick={() => saveTimetable(true)} disabled={saving}>
                            Save anyway
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default ClassTimetablePage;
